import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useId, useMemo, useState } from "react";
import {
  ArrowUpRight,
  CheckCircle2,
  Coins,
  Droplets,
  ExternalLink,
  Loader,
  Lock,
  MapPin,
  Sprout,
  Sun,
  X,
  Zap,
} from "lucide-react";
import { I18nProvider, useI18n } from "@/lib/i18n";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { EcosystemSwitcher, EcosystemHub } from "@/components/EcosystemHub";
import villageMark from "@/assets/ecosystem/villagepando.jpeg.asset.json";
import { track } from "@/lib/analytics";
import { supabase } from "@/integrations/supabase/client";
import { useModalA11y } from "@/hooks/use-modal-a11y";
import { cn } from "@/lib/utils";
import { PandoNexusCockpit } from "@/components/villagepando/PandoNexusCockpit";
import { RwaLiveDashboard } from "@/components/villagepando/RwaLiveDashboard";
import { EcosystemSwap } from "@/components/villagepando/EcosystemSwap";
import { EcosystemPortals } from "@/components/villagepando/EcosystemPortals";
import { absUrl } from "@/lib/seo-jsonld";

export const Route = createFileRoute("/villagepando")({
  head: () => ({
    meta: [
      { title: "VillagePando — Ecosystem Governance Dashboard" },
      {
        name: "description",
        content:
          "Master governance hub for the VillagePando real-world asset network: MEAL24, SolarPando, PlantyPando and AquaPando — unified TAVL, cross-token flow, and portal access.",
      },
      { property: "og:title", content: "VillagePando — Ecosystem Governance Dashboard" },
      {
        property: "og:description",
        content:
          "TAVL, physical anchor map, and inter-token flow across the VillagePando real-world asset ecosystem.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: absUrl("/villagepando") },
      { property: "og:image", content: absUrl("/og-image.jpg") },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: absUrl("/og-image.jpg") },
    ],
    links: [{ rel: "canonical", href: absUrl("/villagepando") }],
  }),
  component: VillagePandoDashboardRoute,
});

function VillagePandoDashboardRoute() {
  return (
    <I18nProvider>
      <Dashboard />
    </I18nProvider>
  );
}

/* ──────────────────────────────────────────────────────────────────── */
/* Data — placeholders ready to wire to live oracles / RPC             */
/* ──────────────────────────────────────────────────────────────────── */

type ModuleKey = "meal24" | "solar" | "planty" | "aqua";

type ModuleDef = {
  key: ModuleKey;
  name: string;
  tagline: string;
  status: "live" | "phase2";
  href: string;
  external?: boolean;
  icon: typeof Sun;
  color: string;
  glow: string;
};

const MODULES: ModuleDef[] = [
  {
    key: "meal24",
    name: "MEAL24",
    tagline: "vp.modules.meal24.tagline",
    status: "live",
    href: "/#tokenomics",
    icon: Coins,
    color: "oklch(0.82 0.18 75)",
    glow: "oklch(0.82 0.18 75 / 0.35)",
  },
  {
    key: "solar",
    name: "SolarPando",
    tagline: "vp.modules.solar.tagline",
    status: "phase2",
    href: "#solar",
    icon: Sun,
    color: "oklch(0.86 0.16 90)",
    glow: "oklch(0.86 0.16 90 / 0.35)",
  },
  {
    key: "planty",
    name: "PlantyPando",
    tagline: "vp.modules.planty.tagline",
    status: "phase2",
    href: "#planty",
    icon: Sprout,
    color: "oklch(0.84 0.16 160)",
    glow: "oklch(0.84 0.16 160 / 0.35)",
  },
  {
    key: "aqua",
    name: "AquaPando",
    tagline: "vp.modules.aqua.tagline",
    status: "phase2",
    href: "#aqua",
    icon: Droplets,
    color: "oklch(0.8 0.14 230)",
    glow: "oklch(0.8 0.14 230 / 0.35)",
  },
];

type Anchor = {
  id: string;
  name: string;
  region: string;
  // normalized 0-100 coords inside the stylized SVG canvas
  x: number;
  y: number;
  status: "active" | "pilot" | "planned";
};

const ANCHORS: Anchor[] = [
  { id: "sandbox-kitchen", name: "SFN Pilot Kitchen", region: "Thailand Sandbox", x: 36, y: 58, status: "active" },
  { id: "sandbox-hub", name: "Sandbox Logistics Hub", region: "Thailand Sandbox", x: 40, y: 52, status: "active" },
  { id: "apac", name: "APAC Expansion Node", region: "Asia-Pacific", x: 50, y: 40, status: "pilot" },
  { id: "mena", name: "MENA Expansion Node", region: "Middle East & North Africa", x: 44, y: 70, status: "pilot" },
  { id: "eu", name: "EU Expansion Node", region: "Europe", x: 40, y: 14, status: "planned" },
  { id: "latam", name: "LATAM Expansion Node", region: "Latin America", x: 24, y: 86, status: "planned" },
];


/* ──────────────────────────────────────────────────────────────────── */
/* Dashboard shell                                                      */
/* ──────────────────────────────────────────────────────────────────── */

function Dashboard() {
  const { t } = useI18n();
  const [openModule, setOpenModule] = useState<ModuleDef | null>(null);

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-[oklch(0.10_0.012_260)] text-foreground">
      {/* Atmospheric backdrop */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10"
        style={{
          background:
            "radial-gradient(70% 50% at 50% -10%, oklch(1 0 0 / 0.05), transparent 60%), radial-gradient(40% 30% at 85% 110%, oklch(0.82 0.18 75 / 0.08), transparent 70%)",
        }}
      />
      <GridPaper />

      <DashHeader />

      <main className="mx-auto max-w-7xl px-4 pb-24 pt-28 sm:px-6">
        <Hero />
        <section id="nexus" className="mt-12">
          <PandoNexusCockpit />
        </section>
        <section id="portals" className="mt-16">
          <EcosystemPortals />
        </section>
        <MacroMonitor />
        <RwaLiveDashboard />
        <EcosystemSwap />
        <section id="map" className="mt-12">
          <SectionTitle
            eyebrow={t("vp.map.eyebrow")}
            title={t("vp.map.title")}
            sub={t("vp.map.sub")}
          />
          <div className="mt-8 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
            <ThailandMap />
            <AnchorList />
          </div>
        </section>

        <section id="flow" className="mt-16">
          <SectionTitle
            eyebrow={t("vp.flow.eyebrow")}
            title={t("vp.flow.title")}
            sub={t("vp.flow.sub")}
          />
          <FlowGraph />
        </section>

        <section id="portals" className="mt-16">
          <SectionTitle
            eyebrow={t("vp.portals.eyebrow")}
            title={t("vp.portals.title")}
            sub={t("vp.portals.sub")}
          />
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {MODULES.map((m) => (
              <PortalCard key={m.key} module={m} onWhitelist={() => setOpenModule(m)} />
            ))}
          </div>
        </section>
      </main>

      <DashFooter />

      <WhitelistModal module={openModule} onClose={() => setOpenModule(null)} />
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────── */
/* Header / Footer                                                      */
/* ──────────────────────────────────────────────────────────────────── */

function DashHeader() {
  const { t } = useI18n();
  return (
    <header className="fixed left-0 right-0 top-0 z-40">
      <div className="mx-4 mt-4 flex max-w-7xl items-center justify-between rounded-full border border-white/10 bg-[oklch(0.12_0.012_260_/_0.7)] px-4 py-2.5 backdrop-blur-xl sm:mx-auto sm:px-5">
        <Link to="/villagepando" className="flex items-center gap-2.5">
          <span className="grid h-9 w-9 place-items-center overflow-hidden rounded-full border border-white/15 bg-[#0B0F17]" style={{ boxShadow: "0 0 22px #10B98155" }}>
            <img src={villageMark.url} alt="VillagePando" className="h-full w-full object-cover" />
          </span>
          <div className="leading-tight">
            <div className="font-display text-sm font-bold tracking-tight">VillagePando <span className="hidden md:inline text-muted-foreground font-normal">| Community RWA</span></div>
            <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground">
              {t("vp.header.subtitle")}
            </div>
          </div>
        </Link>
        <nav className="hidden items-center gap-6 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground md:flex">
          <a href="#monitor" className="hover:text-foreground">{t("vp.nav.monitor")}</a>
          <a href="#map" className="hover:text-foreground">{t("vp.nav.map")}</a>
          <a href="#flow" className="hover:text-foreground">{t("vp.nav.flow")}</a>
          <a href="#portals" className="hover:text-foreground">{t("vp.nav.portals")}</a>
        </nav>
        <div className="flex items-center gap-2">
          <EcosystemSwitcher activeId="village" />
          <LanguageSwitcher />
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/[0.04] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-foreground/80 transition-colors hover:bg-white/[0.08]"
          >
            {t("vp.header.mealSiteLink")}
            <ExternalLink className="h-3 w-3" />
          </Link>
        </div>
      </div>
    </header>
  );
}

function DashFooter() {
  const { t } = useI18n();
  return (
    <footer className="mx-auto max-w-7xl px-4 pb-10 pt-2 sm:px-6">
      <EcosystemHub activeId="village" className="mb-6" />
      <div className="flex flex-col items-start justify-between gap-3 border-t border-white/5 pt-6 text-xs text-muted-foreground sm:flex-row sm:items-center">
        <span className="font-mono uppercase tracking-[0.18em]">
          {t("vp.footer.tagline")}
        </span>
        <span className="font-mono uppercase tracking-[0.18em]">
          {t("vp.footer.networkPrefix")} {new Date().getUTCFullYear()}
        </span>
      </div>
    </footer>
  );
}

/* ──────────────────────────────────────────────────────────────────── */
/* Hero / Macro                                                         */
/* ──────────────────────────────────────────────────────────────────── */

function Hero() {
  const { t } = useI18n();
  return (
    <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.02] p-8 sm:p-12">
      <div
        aria-hidden
        className="pointer-events-none absolute -right-32 -top-32 h-80 w-80 rounded-full blur-3xl"
        style={{ background: "oklch(1 0 0 / 0.08)" }}
      />
      <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-foreground/60">
        {t("vp.hero.eyebrow")}
      </span>
      <h1 className="mt-3 max-w-3xl font-display text-4xl font-medium leading-[1.05] tracking-tight sm:text-5xl md:text-6xl">
        {t("vp.hero.titleLine1")}
        <br />
        <span className="text-foreground/60">{t("vp.hero.titleLine2")}</span>
      </h1>
      <p className="mt-5 max-w-2xl text-base text-muted-foreground">
        {t("vp.hero.body")}
      </p>
    </div>
  );
}

function MacroMonitor() {
  const { t } = useI18n();
  const MACRO_STATS = [
    { label: t("vp.macro.stat.tavl.label"), value: "$1.24M", delta: t("vp.macro.stat.tavl.delta"), tone: "neutral" as const },
    { label: t("vp.macro.stat.anchors.label"), value: "2", sub: t("vp.macro.stat.anchors.sub"), tone: "live" as const },
    { label: t("vp.macro.stat.meals.label"), value: "84,210", sub: t("vp.macro.stat.meals.sub"), tone: "neutral" as const },
    { label: t("vp.macro.stat.submodules.label"), value: "1 / 4", sub: t("vp.macro.stat.submodules.sub"), tone: "phase2" as const },
  ];
  return (
    <section id="monitor" className="mt-12">
      <SectionTitle
        eyebrow={t("vp.macro.eyebrow")}
        title={t("vp.macro.title")}
        sub={t("vp.macro.sub")}
      />
      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {MACRO_STATS.map((s) => (
          <div
            key={s.label}
            className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02] p-5 transition-colors hover:bg-white/[0.035]"
          >
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              {s.label}
            </div>
            <div className="mt-3 font-display text-3xl font-semibold tracking-tight">
              {s.value}
            </div>
            <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
              <span
                className={cn(
                  "inline-block h-1.5 w-1.5 rounded-full",
                  s.tone === "live" && "bg-emerald-400 shadow-[0_0_8px_oklch(0.78_0.18_150_/_0.8)]",
                  s.tone === "phase2" && "bg-amber-400",
                  s.tone === "neutral" && "bg-white/40",
                )}
              />
              {"delta" in s ? s.delta : s.sub}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────── */
/* Map                                                                  */
/* ──────────────────────────────────────────────────────────────────── */

function ThailandMap() {
  const { t } = useI18n();
  const [hover, setHover] = useState<string | null>(null);
  // Stylized Thailand silhouette (simplified). Coordinates are tuned for narrative,
  // not cartographic accuracy.
  return (
    <div
      className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.015] p-4 sm:p-6"
      style={{ minHeight: 460 }}
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            {t("vp.map.canvasEyebrow")}
          </div>
          <div className="mt-1 font-display text-lg font-semibold">
            {t("vp.map.canvasTitle")}
          </div>
        </div>
        <Legend />
      </div>
      <svg
        viewBox="0 0 100 110"
        className="mt-4 h-[380px] w-full"
        role="img"
        aria-label={t("vp.map.ariaLabel")}
      >
        <defs>
          <radialGradient id="vp-pulse" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="oklch(0.82 0.18 75)" stopOpacity="0.6" />
            <stop offset="100%" stopColor="oklch(0.82 0.18 75)" stopOpacity="0" />
          </radialGradient>
          <pattern id="vp-grid" width="5" height="5" patternUnits="userSpaceOnUse">
            <path d="M 5 0 L 0 0 0 5" fill="none" stroke="oklch(1 0 0 / 0.05)" strokeWidth="0.2" />
          </pattern>
        </defs>
        <rect width="100" height="110" fill="url(#vp-grid)" />
        {/* Simplified Thailand silhouette */}
        <path
          d="M40 6 Q46 8 47 14 Q44 22 45 28 Q49 32 52 36 Q57 40 56 46 Q52 50 49 54 Q42 58 38 64 Q34 70 36 76 Q39 82 34 88 Q28 94 24 100 Q22 104 26 106 Q30 104 32 100 Q36 92 41 86 Q46 78 49 70 Q56 60 60 50 Q62 42 58 34 Q54 28 52 22 Q50 14 46 8 Z"
          fill="oklch(1 0 0 / 0.04)"
          stroke="oklch(1 0 0 / 0.18)"
          strokeWidth="0.4"
        />
        {/* Anchors */}
        {ANCHORS.map((a) => {
          const isHover = hover === a.id;
          const color =
            a.status === "active"
              ? "oklch(0.82 0.18 75)"
              : a.status === "pilot"
                ? "oklch(0.85 0.14 90)"
                : "oklch(1 0 0 / 0.5)";
          return (
            <g
              key={a.id}
              transform={`translate(${a.x} ${a.y})`}
              onMouseEnter={() => setHover(a.id)}
              onMouseLeave={() => setHover(null)}
              className="cursor-pointer"
            >
              {a.status === "active" && (
                <>
                  <circle r="6" fill="url(#vp-pulse)">
                    <animate attributeName="r" values="3;8;3" dur="2.4s" repeatCount="indefinite" />
                    <animate
                      attributeName="opacity"
                      values="0.8;0;0.8"
                      dur="2.4s"
                      repeatCount="indefinite"
                    />
                  </circle>
                </>
              )}
              <circle
                r={isHover ? 1.6 : 1.1}
                fill={color}
                stroke="oklch(0.1 0.012 260)"
                strokeWidth="0.3"
                style={{ filter: `drop-shadow(0 0 4px ${color})` }}
              />
              {(isHover || a.status === "active") && (
                <g transform="translate(2.5 -1)">
                  <rect
                    x="0"
                    y="-2.4"
                    rx="0.6"
                    width={a.name.length * 0.95 + 2}
                    height="3.4"
                    fill="oklch(0.1 0.012 260 / 0.9)"
                    stroke="oklch(1 0 0 / 0.15)"
                    strokeWidth="0.15"
                  />
                  <text x="1" y="0" fontSize="1.6" fill="oklch(1 0 0 / 0.9)" fontFamily="ui-monospace, monospace">
                    {a.name}
                  </text>
                </g>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function Legend() {
  const { t } = useI18n();
  return (
    <div className="hidden items-center gap-3 font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground sm:flex">
      <LegendDot color="oklch(0.82 0.18 75)" label={t("vp.map.legend.active")} />
      <LegendDot color="oklch(0.85 0.14 90)" label={t("vp.map.legend.pilot")} />
      <LegendDot color="oklch(1 0 0 / 0.5)" label={t("vp.map.legend.planned")} />
    </div>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className="inline-block h-2 w-2 rounded-full"
        style={{ background: color, boxShadow: `0 0 6px ${color}` }}
      />
      {label}
    </span>
  );
}

function AnchorList() {
  const { t } = useI18n();
  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.015] p-5 sm:p-6">
      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
        {t("vp.map.rosterEyebrow")}
      </div>
      <div className="mt-1 font-display text-lg font-semibold">{t("vp.map.rosterTitle")}</div>
      <ul className="mt-4 space-y-2.5">
        {ANCHORS.map((a) => (
          <li
            key={a.id}
            className="flex items-center justify-between gap-3 rounded-xl border border-white/5 bg-white/[0.02] px-3 py-2.5"
          >
            <div className="flex min-w-0 items-center gap-2.5">
              <MapPin
                className="h-3.5 w-3.5 shrink-0"
                style={{
                  color:
                    a.status === "active"
                      ? "oklch(0.82 0.18 75)"
                      : a.status === "pilot"
                        ? "oklch(0.85 0.14 90)"
                        : "oklch(1 0 0 / 0.5)",
                }}
              />
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-foreground">{a.name}</div>
                <div className="truncate font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                  {a.region}
                </div>
              </div>
            </div>
            <span
              className={cn(
                "shrink-0 rounded-full border px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.18em]",
                a.status === "active" &&
                  "border-[oklch(0.82_0.18_75_/_0.45)] bg-[oklch(0.82_0.18_75_/_0.08)] text-[oklch(0.92_0.14_85)]",
                a.status === "pilot" &&
                  "border-[oklch(0.85_0.14_90_/_0.4)] bg-[oklch(0.85_0.14_90_/_0.06)] text-[oklch(0.9_0.12_90)]",
                a.status === "planned" && "border-white/15 bg-white/[0.04] text-muted-foreground",
              )}
            >
              {t(`vp.map.status.${a.status}`)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────── */
/* Flow graph                                                           */
/* ──────────────────────────────────────────────────────────────────── */

type FlowNode = {
  id: ModuleKey;
  label: string;
  sublabel: string;
  x: number;
  y: number;
  color: string;
  icon: typeof Sun;
};

const NODES: FlowNode[] = [
  { id: "solar", label: "SolarPando", sublabel: "Energy", x: 12, y: 50, color: "oklch(0.86 0.16 90)", icon: Sun },
  { id: "meal24", label: "MEAL24", sublabel: "Kitchen anchor", x: 50, y: 50, color: "oklch(0.82 0.18 75)", icon: Coins },
  { id: "planty", label: "PlantyPando", sublabel: "Ingredients", x: 88, y: 22, color: "oklch(0.84 0.16 160)", icon: Sprout },
  { id: "aqua", label: "AquaPando", sublabel: "Water", x: 88, y: 78, color: "oklch(0.8 0.14 230)", icon: Droplets },
];

type FlowEdge = { from: ModuleKey; to: ModuleKey; label: string; color: string; delay: number };

const EDGES: FlowEdge[] = [
  { from: "solar", to: "meal24", label: "kWh → kitchen", color: "oklch(0.86 0.16 90)", delay: 0 },
  { from: "planty", to: "meal24", label: "ingredients → menu", color: "oklch(0.84 0.16 160)", delay: 0.6 },
  { from: "aqua", to: "meal24", label: "filtered water → service", color: "oklch(0.8 0.14 230)", delay: 1.2 },
  { from: "meal24", to: "planty", label: "MEAL24 → farmgate revenue", color: "oklch(0.82 0.18 75)", delay: 1.8 },
];

function nodeById(id: ModuleKey) {
  const n = NODES.find((x) => x.id === id)!;
  return n;
}

function FlowGraph() {
  const { t } = useI18n();
  const localNodes = NODES.map((n) => ({ ...n, sublabel: t(`vp.flowGraph.node.${n.id}.sublabel`) }));
  const edgeLabelMap: Record<string, string> = {
    "solar->meal24": t("vp.flowGraph.edge.solarToMeal"),
    "planty->meal24": t("vp.flowGraph.edge.plantyToMeal"),
    "aqua->meal24": t("vp.flowGraph.edge.aquaToMeal"),
    "meal24->planty": t("vp.flowGraph.edge.mealToPlanty"),
  };
  return (
    <div className="mt-8 overflow-hidden rounded-3xl border border-white/10 bg-white/[0.015] p-4 sm:p-6">
      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
        {t("vp.flowGraph.eyebrow")}
      </div>
      <svg viewBox="0 0 100 100" className="mt-4 h-[420px] w-full" role="img" aria-label={t("vp.flowGraph.ariaLabel")}>
        <defs>
          {NODES.map((n) => (
            <radialGradient id={`glow-${n.id}`} key={n.id} cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor={n.color} stopOpacity="0.5" />
              <stop offset="100%" stopColor={n.color} stopOpacity="0" />
            </radialGradient>
          ))}
        </defs>
        {/* Edges */}
        {EDGES.map((e, i) => {
          const a = nodeById(e.from);
          const b = nodeById(e.to);
          const pathId = `edge-${i}`;
          // gentle quadratic curve through midpoint with slight vertical bias
          const mx = (a.x + b.x) / 2;
          const my = (a.y + b.y) / 2 + (e.from === "meal24" || e.to === "meal24" ? 0 : 8) * (i % 2 === 0 ? -1 : 1);
          const d = `M ${a.x} ${a.y} Q ${mx} ${my} ${b.x} ${b.y}`;
          return (
            <g key={i}>
              <path id={pathId} d={d} fill="none" stroke={e.color} strokeOpacity="0.25" strokeWidth="0.4" />
              {/* particles */}
              {[0, 0.33, 0.66].map((offset, pi) => (
                <circle key={pi} r="0.7" fill={e.color} style={{ filter: `drop-shadow(0 0 3px ${e.color})` }}>
                  <animateMotion dur="4s" repeatCount="indefinite" begin={`${e.delay + offset * 4}s`}>
                    <mpath href={`#${pathId}`} />
                  </animateMotion>
                  <animate
                    attributeName="opacity"
                    values="0;1;1;0"
                    keyTimes="0;0.1;0.9;1"
                    dur="4s"
                    begin={`${e.delay + offset * 4}s`}
                    repeatCount="indefinite"
                  />
                </circle>
              ))}
              {/* edge label near midpoint */}
              <text
                x={mx}
                y={my - 2}
                fontSize="1.7"
                textAnchor="middle"
                fill="oklch(1 0 0 / 0.55)"
                fontFamily="ui-monospace, monospace"
              >
                {edgeLabelMap[`${e.from}->${e.to}`] ?? e.label}
              </text>
            </g>
          );
        })}
        {/* Nodes */}
        {localNodes.map((n) => (
          <g key={n.id} transform={`translate(${n.x} ${n.y})`}>
            <circle r="10" fill={`url(#glow-${n.id})`} />
            <circle r="4.5" fill="oklch(0.1 0.012 260)" stroke={n.color} strokeWidth="0.35" />
            <text
              x="0"
              y="9"
              fontSize="2.2"
              textAnchor="middle"
              fill="oklch(1 0 0 / 0.92)"
              fontFamily="ui-sans-serif, system-ui"
              fontWeight="600"
            >
              {n.label}
            </text>
            <text
              x="0"
              y="12.2"
              fontSize="1.5"
              textAnchor="middle"
              fill="oklch(1 0 0 / 0.55)"
              fontFamily="ui-monospace, monospace"
            >
              {n.sublabel}
            </text>
          </g>
        ))}
      </svg>
      <div className="mt-2 flex flex-wrap items-center gap-3 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
        <Zap className="h-3 w-3" />
        {t("vp.flowGraph.caption")}
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────── */
/* Portal cards                                                         */
/* ──────────────────────────────────────────────────────────────────── */

function PortalCard({
  module: m,
  onWhitelist,
}: {
  module: ModuleDef;
  onWhitelist: () => void;
}) {
  const { t } = useI18n();
  const Icon = m.icon;
  const live = m.status === "live";
  return (
    <article
      className="group relative flex flex-col overflow-hidden rounded-3xl border border-white/10 bg-white/[0.02] p-5 transition-shadow duration-500 hover:shadow-[0_30px_80px_-20px_oklch(0_0_0_/_0.6)]"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full blur-3xl opacity-50 transition-opacity duration-500 group-hover:opacity-90"
        style={{ background: m.glow }}
      />
      <div className="relative z-10 flex items-start justify-between gap-2">
        <span
          className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em]"
          style={{
            borderColor: `${m.color.replace(")", " / 0.4)")}`,
            color: m.color,
            background: `${m.color.replace(")", " / 0.06)")}`,
          }}
        >
          <Icon className="h-3 w-3" />
          {live ? t("vp.portalCard.badge.live") : t("vp.portalCard.badge.phase2")}
        </span>
        {!live && <Lock className="h-3.5 w-3.5 text-muted-foreground" />}
      </div>
      <h3 className="relative z-10 mt-4 font-display text-xl font-semibold tracking-tight">
        {m.name}
      </h3>
      <p className="relative z-10 mt-1.5 text-xs leading-relaxed text-muted-foreground">
        {t(m.tagline)}
      </p>

      <div className="relative z-10 mt-5">
        {live ? (
          <Link
            to="/"
            onClick={() => track("cta_click", { id: `portal_${m.key}`, surface: "vp_dashboard" })}
            className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-white/15 bg-white/[0.06] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-foreground transition-colors hover:bg-white/[0.1]"
          >
            {t("vp.portalCard.openPortal")}
            <ArrowUpRight className="h-3 w-3" />
          </Link>
        ) : (
          <div className="space-y-2">
            <Link
              to="/villagepando/$asset"
              params={{ asset: m.key }}
              onClick={() =>
                track("cta_click", { id: `portal_contribute_${m.key}`, surface: "vp_dashboard" })
              }
              className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl px-3 py-2 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-obsidian transition-transform duration-200 hover:scale-[1.01]"
              style={{
                background: `linear-gradient(135deg, ${m.color}, oklch(0.95 0.08 95))`,
                boxShadow: `0 10px 30px -10px ${m.color.replace(")", " / 0.55)")}`,
              }}
            >
              {t("vp.portalCard.contributePrefix")} {m.name.replace("Pando", "").toUpperCase()}
              <ArrowUpRight className="h-3 w-3" />
            </Link>
            <button
              type="button"
              onClick={() => {
                track("cta_click", { id: `portal_whitelist_${m.key}`, surface: "vp_dashboard" });
                onWhitelist();
              }}
              className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-white/15 bg-white/[0.04] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-foreground/80 transition-colors hover:bg-white/[0.08]"
            >
              {t("vp.portalCard.joinWhitelist")}
              <ArrowUpRight className="h-3 w-3" />
            </button>
          </div>
        )}
      </div>
    </article>
  );
}

/* ──────────────────────────────────────────────────────────────────── */
/* Reusable bits                                                        */
/* ──────────────────────────────────────────────────────────────────── */

function SectionTitle({
  eyebrow,
  title,
  sub,
}: {
  eyebrow: string;
  title: string;
  sub: string;
}) {
  return (
    <div className="max-w-2xl">
      <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-foreground/60">
        {eyebrow}
      </span>
      <h2 className="mt-2 font-display text-3xl font-medium leading-tight tracking-tight sm:text-4xl">
        {title}
      </h2>
      <p className="mt-3 text-sm text-muted-foreground">{sub}</p>
    </div>
  );
}

function GridPaper() {
  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 -z-10 opacity-[0.06]"
      style={{
        backgroundImage:
          "linear-gradient(oklch(1 0 0) 1px, transparent 1px), linear-gradient(90deg, oklch(1 0 0) 1px, transparent 1px)",
        backgroundSize: "48px 48px",
        maskImage: "radial-gradient(ellipse at center, black, transparent 75%)",
      }}
    />
  );
}

/* ──────────────────────────────────────────────────────────────────── */
/* Whitelist modal                                                      */
/* ──────────────────────────────────────────────────────────────────── */

function WhitelistModal({
  module: m,
  onClose,
}: {
  module: ModuleDef | null;
  onClose: () => void;
}) {
  const { lang, t } = useI18n();
  const open = !!m && m.status !== "live";
  const containerRef = useModalA11y({
    open,
    onClose,
    analyticsId: `vp_dash_modal_${m?.key ?? "none"}`,
  });
  const titleId = useId();
  const [email, setEmail] = useState("");
  const [wallet, setWallet] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "ok" | "error">("idle");
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setEmail("");
      setWallet("");
      setStatus("idle");
      setErr(null);
    }
  }, [open, m?.key]);

  const moduleKey = useMemo(() => {
    if (!m) return null;
    if (m.key === "meal24") return null;
    return m.key;
  }, [m]);

  if (!open || !m || !moduleKey) return null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("submitting");
    setErr(null);
    const { error } = await supabase.from("villagepando_whitelist").insert({
      module: moduleKey,
      email: email.trim(),
      wallet: wallet.trim() || null,
      lang,
    });
    if (error) {
      setStatus("error");
      setErr(error.message);
      return;
    }
    setStatus("ok");
  };

  return (
    <div
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
    >
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative w-full max-w-md overflow-hidden rounded-3xl border border-white/10 bg-[oklch(0.10_0.012_260_/_0.97)] p-7 shadow-[0_50px_120px_-40px_oklch(0_0_0)]"
      >
        <div
          aria-hidden
          className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full blur-3xl opacity-60"
          style={{ background: m.glow }}
        />
        <button
          type="button"
          onClick={onClose}
          aria-label={t("vp.whitelistModal.closeAria")}
          className="absolute right-4 top-4 z-10 grid h-9 w-9 place-items-center rounded-full border border-white/10 bg-white/[0.04] text-muted-foreground transition-colors hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="relative z-10">
          <span
            className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em]"
            style={{
              borderColor: `${m.color.replace(")", " / 0.4)")}`,
              color: m.color,
              background: `${m.color.replace(")", " / 0.06)")}`,
            }}
          >
            <m.icon className="h-3 w-3" />
            {t("vp.whitelistModal.badge")}
          </span>
          <h3 id={titleId} className="mt-3 font-display text-2xl font-semibold tracking-tight">
            {t("vp.whitelistModal.titlePrefix")} {m.name} {t("vp.whitelistModal.titleSuffix")}
          </h3>
          <p className="mt-2 text-sm text-muted-foreground">
            {t("vp.whitelistModal.body")}
          </p>

          {status === "ok" ? (
            <div className="mt-6 flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" style={{ color: m.color }} />
              <div>
                <div className="font-display text-base font-semibold">{t("vp.whitelistModal.successTitle")}</div>
                <div className="mt-1 text-sm text-muted-foreground">
                  {t("vp.whitelistModal.successBody")}
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-3">
              <Field
                id="vpd-email"
                type="email"
                required
                label={t("vp.whitelistModal.fields.email")}
                value={email}
                onChange={setEmail}
                color={m.color}
                placeholder={t("vp.whitelistModal.fields.emailPlaceholder")}
              />
              <Field
                id="vpd-wallet"
                label={t("vp.whitelistModal.fields.wallet")}
                value={wallet}
                onChange={setWallet}
                color={m.color}
                placeholder={t("vp.whitelistModal.fields.walletPlaceholder")}
              />
              {err && (
                <p className="text-xs text-coral" role="alert">
                  {err}
                </p>
              )}
              <button
                type="submit"
                disabled={status === "submitting" || !email}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-bold uppercase tracking-wide text-obsidian transition-transform duration-200 hover:scale-[1.01] disabled:opacity-60"
                style={{ background: `linear-gradient(135deg, ${m.color}, oklch(0.95 0.08 90))` }}
              >
                {status === "submitting" ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    {t("vp.whitelistModal.submitting")}
                  </>
                ) : (
                  t("vp.whitelistModal.submit")
                )}
              </button>
              <p className="pt-2 font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground">
                {t("vp.whitelistModal.disclaimer")}
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({
  id,
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  required,
  color,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  required?: boolean;
  color: string;
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground"
      >
        {label}
      </label>
      <input
        id={id}
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1.5 w-full rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2.5 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground/50 focus:border-white/30 focus-visible:ring-2"
        style={{ ["--tw-ring-color" as string]: `${color.replace(")", " / 0.5)")}` } as React.CSSProperties}
      />
    </div>
  );
}
